import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Download, Loader2, ReceiptText } from "lucide-react";
import { CockpitAreaGate } from "@/components/cockpit/CockpitAreaGate";
import { CockpitPageShell } from "@/components/cockpit/CockpitPageShell";
import { CockpitPageHeader } from "@/components/cockpit/CockpitPageHeader";
import { CockpitSection } from "@/components/cockpit/CockpitSection";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadMyInvoice, listMyPurchases } from "@/lib/cockpit-purchases.functions";
import type { OwnPurchase } from "@/lib/cockpit-purchases.functions";

export const Route = createFileRoute("/_cockpit/cockpit/kaeufe")({
  head: () => ({
    meta: [
      { title: "Meine Käufe – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <CockpitAreaGate area="purchases">
      <CockpitPurchasesPage />
    </CockpitAreaGate>
  ),
});

function formatDate(value: string | null): string {
  if (!value) return "–";
  return new Date(value).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency?.toUpperCase() || "EUR",
  }).format(cents / 100);
}

function CockpitPurchasesPage() {
  const load = useServerFn(listMyPurchases);
  const download = useServerFn(downloadMyInvoice);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["cockpit", "purchases"],
    queryFn: () => load({ data: undefined as never }),
  });

  const purchases: OwnPurchase[] = data ?? [];

  const onDownload = async (purchase: OwnPurchase) => {
    setBusyId(purchase.id);
    try {
      const doc = await download({ data: { purchaseId: purchase.id } });
      // Das Dokument wird nur im Browser zusammengesetzt und sofort gespeichert –
      // bei uns bleibt keine Kopie zurück.
      const bytes = Uint8Array.from(atob(doc.contentBase64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      const message =
        error instanceof Error && error.message && !/Forbidden|Unauthorized/i.test(error.message)
          ? error.message
          : "Die Rechnung konnte nicht geladen werden. Bitte versuchen Sie es später erneut.";
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <CockpitPageShell>
      <CockpitPageHeader
        title="Meine Käufe"
        description="Ihre Käufe mit Datum, Bezeichnung, Betrag und Rechnungsnummer. Die Rechnung laden Sie hier direkt herunter."
      />

      <CockpitSection title="Übersicht">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : purchases.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Für Ihr Konto liegt derzeit kein Kauf vor.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {purchases.map((purchase) => (
              <li
                key={purchase.id}
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="font-display text-base text-primary">{purchase.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {formatDate(purchase.purchasedAt)} ·{" "}
                    {formatAmount(purchase.amountCents, purchase.currency)}
                    {purchase.invoiceNumber ? ` · Rechnung ${purchase.invoiceNumber}` : ""}
                  </p>
                </div>
                {purchase.downloadable ? (
                  <Button
                    variant="outline"
                    onClick={() => onDownload(purchase)}
                    disabled={busyId === purchase.id}
                    className="shrink-0"
                  >
                    {busyId === purchase.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Rechnung herunterladen
                  </Button>
                ) : (
                  <span className="inline-flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
                    <ReceiptText className="h-4 w-4" aria-hidden="true" />
                    Rechnung in Vorbereitung
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CockpitSection>
    </CockpitPageShell>
  );
}
